import React, { useRef, useEffect, useCallback } from "react";
import { Button, Tooltip } from "@mantine/core";
import TextareaAutosize from "react-textarea-autosize";
import { Refresh, Copy, ExternalLink, ScreenShare } from "tabler-icons-react";
import { useChat } from "./contexts/ChatContext";
import { useChatService } from "./services/ChatService";

const SidebarChat = ({
  open,
  onClose,
  conversationID,
  initialSuggestions = [],
  fileIds = null, // NEW: Accept fileIds for multi-doc chat
}) => {
  const chat = useChat();
  const chatService = useChatService();

  // Refs
  const latestMessageRef = useRef(null);
  const inputRef = useRef(null);

  // save previous open state to detect close transitions
  const prevOpenRef = useRef(open);

  // Save draft when chat is closed (either by button or parent toggling open -> false)
  useEffect(() => {
    prevOpenRef.current = open;
  }, [open]);

  // handleClose: called by close button to save and then call parent's onClose
  const handleClose = useCallback(() => {
    chat.closeChat();
    if (onClose) onClose();
  }, [chat, onClose]);

  // Initialize chat when props change
  useEffect(() => {
    if (open && conversationID) {
      chatService.initializeChat(conversationID, initialSuggestions, fileIds); // Pass fileIds
    }
  }, [conversationID, initialSuggestions, fileIds, open, chatService]);

  useEffect(() => {
    // Reset error when opened
    if (open) {
      chat.setError(null);
    }
  }, [open, chat.setError]);

  useEffect(() => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [chat.messages]);

  // Handle actions using chat service
  const handleSend = async (questionText = chat.inputDraft) => {
    await chatService.sendMessage(questionText);
  };

  const handleSuggestionClick = async (suggestion, conversationID) => {
    await chatService.handleSuggestionClick(suggestion, conversationID);
  };

  const handleCopyMessage = (text) => {
    chatService.copyMessage(text);
  };

  const handleRerunMessage = async (previousQuestion, conversationID) => {
    await chatService.rerunMessage(previousQuestion, conversationID);
  };

  const handlePopOut = () => {
    // Set chat mode to pop up
    chat.openPopup();
    // Close sidebar when opening popup
    if (onClose) onClose();
  };

  const handleNewWindow = () => {
    // Set chat mode to new window
    chat.openNewWindow();
    // Close sidebar when opening new window
    if (onClose) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Compute the latest AI message id
  const latestAIMessageId = chatService.getLatestAIMessageId();

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        // width: 360,
        height: "100vh",
        background: "#fff",
        boxShadow: "-2px 0 8px rgba(0,0,0,0.08)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.3s",
        fontFamily: "Lexend",
      }}
      className="w-full sm:w-full md:w-1/2 lg:w-1/2"
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px",
          borderBottom: "1px solid #eee",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "orange",
            marginRight: 12,
          }}
        />
        <div style={{ fontWeight: 600, fontSize: 18, flex: 1 }}>lawyal.ai</div>

        <Tooltip label="Pop out chat">
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            onClick={handlePopOut}
            style={{
              marginRight: 8,
              padding: "4px 8px",
              minWidth: "auto",
            }}
          >
            <ExternalLink size={16} />
          </Button>
        </Tooltip>

        <Tooltip label="Open in new window">
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            onClick={handleNewWindow}
            style={{
              marginRight: 8,
              padding: "4px 8px",
              minWidth: "auto",
            }}
          >
            <ScreenShare size={16} />
          </Button>
        </Tooltip>

        <button
          onClick={handleClose}
          style={{
            background: "none",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      </div>
      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chat.messages.map((msg, index) => (
          <React.Fragment key={msg.id}>
            <div
              ref={index === chat.messages.length - 1 ? latestMessageRef : null}
              className={`
                ${
                  msg.sender === "user"
                    ? "self-end bg-indigo-100"
                    : "self-start bg-gray-50"
                }
                rounded-lg p-4 mb-2 max-w-[80%] ${
                  msg.sender === "user" ? "whitespace-pre-line" : ""
                }
              `}
              style={{
                lineHeight: "1.6",
                fontSize: "15px",
                fontFamily: "Lexend, sans-serif",
              }}
            >
              {msg.isLoading ? (
                <div className="flex items-center space-x-1">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              ) : msg.sender === "ai" ? (
                <div
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                  className="prose prose-sm max-w-none ai-message"
                />
              ) : (
                msg.text
              )}
            </div>

            {/* Copy and Rerun buttons for AI messages */}
            {msg.sender === "ai" && !msg.isLoading && (
              <div className="flex gap-2 mb-4 ml-2">
                <Tooltip label="Copy message">
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => handleCopyMessage(msg.text)}
                    styles={{
                      root: {
                        height: "28px",
                        width: "28px",
                        padding: "0",
                        minWidth: "unset",
                      },
                    }}
                  >
                    <Copy size={14} />
                  </Button>
                </Tooltip>

                <Tooltip label="Regenerate response">
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => {
                      // Find the previous user message to rerun
                      const previousUserMsg = chat.messages
                        .slice(0, index)
                        .reverse()
                        .find((m) => m.sender === "user");
                      if (previousUserMsg) {
                        handleRerunMessage(
                          previousUserMsg.text,
                          msg.conversationID
                        );
                      }
                    }}
                    // disable rerun for all but the latest AI message
                    disabled={chat.loading || msg.id !== latestAIMessageId}
                    styles={{
                      root: {
                        height: "28px",
                        width: "28px",
                        padding: "0",
                        minWidth: "unset",
                      },
                    }}
                  >
                    <Refresh size={14} />
                  </Button>
                </Tooltip>
              </div>
            )}

            {/* Document References */}
            {msg.sender === "ai" &&
              !msg.isLoading &&
              msg.documentReferences &&
              msg.documentReferences.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 12,
                    padding: 12,
                    backgroundColor: "#f9fafb",
                    borderRadius: 6,
                    fontSize: "13px",
                    maxWidth: "80%",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#6b7280",
                    }}
                  >
                    📄 Sources:
                  </div>
                  {msg.documentReferences.map((ref, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: 8,
                        marginBottom: 6,
                        borderLeft: "2px solid #3b82f6",
                        paddingLeft: 10,
                      }}
                    >
                      <div style={{ fontWeight: 500, color: "#374151" }}>
                        {ref.file_name || `Document ${ref.file_id}`}
                      </div>
                      {ref.excerpt && (
                        <div
                          style={{
                            color: "#6b7280",
                            marginTop: 4,
                            fontStyle: "italic",
                          }}
                        >
                          "{ref.excerpt}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            <div className="max-w-[80%]">
              {msg.sender === "ai" &&
                !msg.isLoading &&
                msg.suggestions &&
                msg.suggestions.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                    className="mb-8"
                  >
                    {msg.suggestions.map((suggestion, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        className="p-2 h-auto"
                        styles={{
                          label: {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            textAlign: "left",
                          },
                        }}
                        // style={{ marginTop: 4, textAlign: "left", justifyContent: "flex-start" }}
                        onClick={() =>
                          handleSuggestionClick(suggestion, msg.conversationID)
                        }
                        disabled={chat.loading}
                        title={suggestion}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
            </div>
          </React.Fragment>
        ))}

        {/* <div ref={messagesEndRef} /> */}
      </div>
      {/* Input */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #eee",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextareaAutosize
          ref={inputRef}
          className="no-resize"
          value={chat.inputDraft}
          onChange={(e) => chatService.updateInputDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question here..."
          minRows={3}
          maxRows={8}
          onHeightChange={() => {
            try {
              if (latestMessageRef.current) {
                latestMessageRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            } catch (e) {
              // ignore
            }
          }}
          style={{
            flex: 1,
            borderRadius: 8,
            border: "1px solid #ddd",
            padding: 8,
            lineHeight: 1.4,
            fontSize: 14,
          }}
          disabled={chat.loading}
        />
        <Button
          onClick={() => {
            handleSend();
          }}
          disabled={chatService.isSendDisabled()}
          styles={{
            root: {
              marginLeft: 8,
              background: "#61dafb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 18px",
              fontWeight: 600,
              cursor: chat.loading ? "not-allowed" : "pointer",
              // when disabled — override appearance here
              "&[data-disabled]": {
                background: "#96def1ff",
                opacity: 1,
                color: "#fff",
                cursor: "not-allowed",
              },
            },
            label: {
              color: "#fff",
            },
          }}
        >
          {"Send"}
        </Button>
      </div>
      {chat.error && (
        <div style={{ color: "red", textAlign: "center", padding: 8 }}>
          {chat.error}
        </div>
      )}
      <style jsx>{`
        .chat-message {
          font-family: "Lexend", sans-serif;
        }
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #6b7280;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%,
          60%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        .prose {
          max-width: none;
          color: #1f2937;
          line-height: 1.7;
          font-size: 15px;
          font-family: "Lexend", sans-serif;
        }
        .prose .list-header {
          font-weight: 600;
          font-size: 1.1em;
          margin-top: 1em;
          color: #111827;
        }
        .prose h1 {
          font-size: 1.8em;
          font-weight: 600;
          margin: 1.5em 0 0.8em 0;
          color: #111827;
        }
        .prose h2 {
          font-size: 1.4em;
          font-weight: 600;
          color: #111827;
          margin: 1.3em 0 0.7em 0;
        }
        .prose h3 {
          font-size: 1.2em;
          font-weight: 600;
          color: #111827;
          margin: 1.1em 0 0.6em 0;
        }
        .prose p {
          margin: 0.75em 0;
          line-height: 1.6;
        }
        .prose strong {
          font-weight: 600;
          color: #111827;
        }
        .prose .section-title {
          font-size: 1.2em;
          font-weight: 600;
          color: #111827;
          margin: 1.2em 0 0.6em 0;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.3em;
        }
        .prose blockquote {
          margin: 1em 0;
          padding: 0.5em 0 0.5em 1em;
          border-left: 3px solid #e5e7eb;
          color: #4b5563;
          font-style: italic;
        }
        .prose ul,
        .prose ol {
          list-style: disc outside !important;
          padding-left: 1.5em !important;
          margin: 1em 0 1em 0 !important;
        }

        .prose li {
          margin-bottom: 0.5em !important;
          text-align: left !important;
          line-height: 1.6;
          padding-left: 0.3em !important;
          margin-left: 0 !important;
        }
        .ai-message h1 {
          font-size: 1.5em;
          font-weight: bold;
          margin-bottom: 0.5em;
          color: #111827;
        }
        .ai-message h2 {
          font-size: 1.25em;
          font-weight: bold;
          margin-bottom: 0.5em;
          color: #1f2937;
        }
        .ai-message h3 {
          font-size: 1.125em;
          font-weight: bold;
          margin-bottom: 0.5em;
          color: #374151;
        }
        .ai-message strong {
          font-weight: 600;
          color: #4f46e5;
        }
        .ai-message em {
          font-style: italic;
          color: #6b7280;
        }
        .ai-message .numbered-item {
          margin-left: 0.5em;
          margin-bottom: 0.5em;
        }
        .ai-message blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          font-style: italic;
          color: #6b7280;
        }
        .ai-message code {
          background-color: #f3f4f6;
          padding: 0.2em 0.4em;
          border-radius: 0.25em;
          font-size: 0.875em;
        }
        .ai-message hr {
          margin: 1em 0;
          border-top: 1px solid #e5e7eb;
        }

        .no-resize,
        textarea.no-resize {
          resize: none !important;
          -webkit-resize: none !important;
          overflow: auto;
        }
      `}</style>
    </div>
  );
};

export default SidebarChat;
